import React, { useMemo } from 'react';
import { useAuthContext } from 'contexts';
import { useGetProfileSidebarUserOfficeAccessQuery } from 'generated/graphql';
import { useProfilePageUserContext } from '../ProfilePageUserContext';
import {
  PermissionsCard,
  PermissionsCardItem,
  PermissionsCardSkeleton,
} from './PermissionsCard';

type PermissionsCardContainerProps = {
  userId: string;
};

const PLACES_MANAGE_PERMISSION = 'places:manage';

export const PermissionsCardContainer = ({
  userId,
}: PermissionsCardContainerProps): JSX.Element | null => {
  const { currentUser, userPermissions } = useAuthContext();
  const isCurrentPerson = currentUser?.id === userId;

  // Visibility mirrors the legacy profile page: passes are shown to the user
  // themselves or to viewers who can view office access; administration,
  // groups and roles require the viewer to manage places.
  const canViewOfficeAccess = userPermissions?.canViewOfficeAccess ?? false;
  const canManagePlaces = userPermissions?.canManagePlaces ?? false;
  const showPasses = isCurrentPerson || canViewOfficeAccess;
  const showOfficeAdmin = canManagePlaces;
  const showCard = showPasses || showOfficeAdmin;

  const { user, managedLocationNames, loading } = useProfilePageUserContext();

  const { data: officeAccessData, loading: passesLoading } =
    useGetProfileSidebarUserOfficeAccessQuery({
      variables: { userId },
      skip: !showCard || !showPasses,
      errorPolicy: 'all',
    });

  const officeAdministration = useMemo<PermissionsCardItem[]>(() => {
    if (!user) {
      return [];
    }
    const hasOrgWideManage =
      user.permissions?.[PLACES_MANAGE_PERMISSION] === true;
    if (hasOrgWideManage) {
      return [{ key: user.account.id, label: user.account.name }];
    }
    return managedLocationNames.map((name, index) => ({
      key: `${index}-${name}`,
      label: name,
    }));
  }, [user, managedLocationNames]);

  const passes = useMemo<PermissionsCardItem[]>(() => {
    const access = officeAccessData?.getUserById?.officeAccess ?? [];
    return access.map((pass, index) => {
      const placeName = pass.location?.name ?? pass.campus?.name ?? '';
      const label = placeName ? `${pass.name} · ${placeName}` : pass.name ?? '';
      return { key: pass.slug ?? `${index}-${label}`, label };
    });
  }, [officeAccessData]);

  const groups = useMemo<PermissionsCardItem[]>(
    () =>
      (user?.groups ?? []).map((group) => ({
        key: group.slug || String(group.id),
        label: group.name,
      })),
    [user]
  );

  const roles = useMemo<PermissionsCardItem[]>(
    () =>
      (user?.roles ?? []).map((role, index) => ({
        key: role.slug || role.id?.toString() || `${index}-${role.name}`,
        label: role.name,
      })),
    [user]
  );

  if (!showCard) {
    return null;
  }

  if (loading && !user) {
    return <PermissionsCardSkeleton />;
  }

  // The card is supplementary; if the org-user read failed entirely there is
  // nothing meaningful to show, so hide it rather than rendering an error.
  if (!user) {
    return null;
  }

  return (
    <PermissionsCard
      showOfficeAdmin={showOfficeAdmin}
      showPasses={showPasses}
      officeAdministration={officeAdministration}
      passes={passes}
      groups={groups}
      roles={roles}
      passesLoading={passesLoading}
    />
  );
};
